import { trackOutboundClick, withUtm } from "../../lib/outboundTracking";

// FounderLinks — reusable, data-driven editorial links block for
// Founder Features. Consumes an article's `brandLinks.links` array; renders
// nothing if the array is empty or absent. No hard-coded platforms, labels,
// URLs or copy live inside this component — publishing a new founder is a
// content-only operation.
//
// See /app/backend/content/README-community.md for the JSON schema.
//
// Design contract (approved during Founder Feature No. 001):
//   • Restrained italic serif links, centred, separated by a bronze mid-dot
//     on desktop and stacked on mobile.
//   • No CTA buttons, no marketing language, no visual chrome.
//   • Each link fires an outbound-click event (PostHog + server beacon) with
//     the founder, brand, feature and destination taken from article data.
export function FounderLinks({ brandLinks, featureId, founder, brand }) {
  if (!brandLinks || !Array.isArray(brandLinks.links) || brandLinks.links.length === 0) {
    return null;
  }

  // Only render links that have both a label and a URL. Any absent platform
  // (LinkedIn, YouTube, etc.) simply does not appear.
  const links = brandLinks.links.filter(
    (l) => l && typeof l.label === "string" && l.label.trim() && typeof l.url === "string" && l.url.trim()
  );
  if (links.length === 0) return null;

  const utm = brandLinks.utm;
  const feature = featureId || (utm && utm.campaign) || null;

  return (
    <footer
      className="mt-24 md:mt-28 pt-12 md:pt-14 border-t border-subtle-custom"
      data-testid="founder-brand-links"
      aria-label={brand ? `${brand} — links` : "Continue"}
    >
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-10 text-center">
        {links.map((link, i) => {
          // The visible destination (used for tracking) is always the real
          // URL, with UTMs applied if requested. The anchor's href is the
          // /go/* redirect when present, so the internal tracking route is
          // hit before the third-party site loads.
          const trackedUrl = link.applyUtm ? withUtm(link.url, utm, link.utmContent) : link.url;
          const href = link.redirect || trackedUrl;

          return (
            <span key={link.platform || i} className="contents">
              {i > 0 && (
                <span className="hidden md:inline text-secondary-custom/30" aria-hidden="true">·</span>
              )}
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackOutboundClick({
                    destination: link.platform || "other",
                    feature,
                    founder: founder || null,
                    brand: brand || null,
                    url: trackedUrl,
                    link_position: link.linkPosition || "footer",
                    collaborator: (typeof brand === "string" ? brand.toLowerCase().replace(/\s+/g, "-") : null),
                  })
                }
                className="font-serif italic text-base md:text-lg text-primary-custom hover:text-[var(--accent-bronze)] transition-colors underline underline-offset-[6px] decoration-[var(--accent-bronze)]/40 hover:decoration-[var(--accent-bronze)]"
                data-testid={`founder-link-${link.platform || "other"}`}
              >
                {link.label}
              </a>
            </span>
          );
        })}
      </div>
    </footer>
  );
}

export default FounderLinks;
